import { useTranslation } from 'react-i18next';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { useModal } from '@/components/providers/ModalProvider/ModalProvider';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import type { Party } from '@/features/party/types';
import useBroker from '@/hooks/useBroker';
import removeHtml from '@/lib/html/removeHtml';
import hasItems from '@/lib/utils/hasItems';

import CreateTaskModal from '../components/CreateTaskModal/CreateTaskModal';
import ExportPartyModal from '../components/ExportPartyModal/ExportPartyModal';
import PickCRMModal from '../components/PickCRMModal/PickCRMModal';
import type { CRM } from '../types';
import isPartyInCrm from '../utils/isPartyInCrm';

export default function useCreateTask() {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const { crms } = useBroker();

  const selectedParty = useSelectedParty();
  const {
    analysis: { analysis },
  } = useEmail();

  const taskCrms = crms ?? [];

  const showCreateTaskModal = (crm: CRM, party: Party) => {
    showModal(
      CreateTaskModal,
      {
        crm,
        defaultValues: {
          title: analysis?.subject,
          description: removeHtml(analysis?.summary_bullets?.[0]),
        },
        party,
      },
      {
        size: 'full',
        title: t('CREATE_TASK_TITLE', {
          crm: t(`CRM_NAME_${crm.toUpperCase()}`),
        }),
      },
    );
  };

  const pickCrmToCreateTaskIn = (party: Party) =>
    showModal(PickCRMModal, {
      onSubmit: (crm: CRM) => showCreateTaskModal(crm, party),
      description: t('PICK_CRM_CREATE_TASK_DESCRIPTION'),
      availableCrms: taskCrms.filter((crm) => isPartyInCrm(party, crm.key)),
    });

  const createTask = () => {
    if (!selectedParty) return;

    const isPartyInAnyCrm = taskCrms.some((crm) =>
      isPartyInCrm(selectedParty, crm.key),
    );

    if (isPartyInAnyCrm) {
      pickCrmToCreateTaskIn(selectedParty);

      return;
    }

    showModal(ExportPartyModal, {
      partyId: selectedParty.id,
      crms: taskCrms,
      titleKey: 'EXPORT_PARTY_NOT_FOUND_TITLE',
      onExported: pickCrmToCreateTaskIn,
    });
  };

  return { canCreateTask: hasItems(taskCrms), createTask };
}
